// ABOUTME: Layout shell for the authenticated app with protected route enforcement
// ABOUTME: Handles auth gating, onboarding redirect, celebrations, and nav switching

import {
  AcademicCapIcon,
  ArrowRightEndOnRectangleIcon,
  EllipsisHorizontalIcon,
  HomeIcon,
  RectangleStackIcon,
  ShoppingBagIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom'
import { LoaderIcon } from '@/components/icons'
import { CelebrationProvider } from '@/components/providers/CelebrationProvider'
import { ROUTES } from '@/constants/routes'
import { useGameCelebrations } from '@/hooks/useGameCelebrations'
import { useProfile } from '@/hooks/useProfile'
import type { User } from '@/providers'
import { getAuthService } from '@/services/AuthService'

const AUTH_EXEMPT_PATHS = new Set<string>(['/home'])

const authenticatedNav = [
  { name: 'Home', href: ROUTES.HOME, childRoutes: [] as string[], icon: HomeIcon },
  {
    name: 'Wardrobe',
    href: ROUTES.WARDROBE,
    childRoutes: [ROUTES.SCANNER, ROUTES.OUTFIT_BUILDER],
    icon: RectangleStackIcon,
  },
  {
    name: 'Learn',
    href: ROUTES.EDUCATION,
    childRoutes: [ROUTES.CHALLENGES],
    icon: AcademicCapIcon,
  },
  { name: 'Shop', href: ROUTES.SHOP, childRoutes: [], icon: ShoppingBagIcon },
  { name: 'AI', href: ROUTES.AI_COACH, childRoutes: [], icon: SparklesIcon },
  {
    name: 'More',
    href: '/more',
    childRoutes: [ROUTES.PROFILE, ROUTES.PROGRESS, ROUTES.INSIGHTS, ROUTES.LEADERBOARD, ROUTES.AVATAR, ROUTES.COLOR_PROFILE, ROUTES.SUBSCRIPTION],
    icon: EllipsisHorizontalIcon,
  },
]

const publicNav = [
  { name: 'Home', href: ROUTES.LANDING, childRoutes: [] as string[], icon: HomeIcon },
  { name: 'Learn', href: ROUTES.EDUCATION, childRoutes: [], icon: AcademicCapIcon },
  { name: 'Sign In', href: ROUTES.AUTH, childRoutes: [], icon: ArrowRightEndOnRectangleIcon },
]

function GameCelebrationWatcher() {
  useGameCelebrations()
  return null
}

export default function AppLayout() {
  const location = useLocation()
  const [user, setUser] = useState<User | null | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const { data: profile, isLoading: profileLoading } = useProfile({ enabled: !!user })
  const onboardingCompleted =
    profile?.onboardingDone || !!profile?.bodyType || !!profile?.preferences

  useEffect(() => {
    const authService = getAuthService()
    authService.getCurrentUser().then((u) => {
      setUser(u)
      setLoading(false)
    })
    const unsubscribe = authService.onAuthStateChange(setUser)
    return () => unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="flex flex-col items-center gap-4">
          <LoaderIcon className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-fg-secondary">Checking authentication...</p>
        </div>
      </div>
    )
  }

  const isExempt = AUTH_EXEMPT_PATHS.has(location.pathname)

  if (!user && !isExempt) {
    return <Navigate to={ROUTES.AUTH} state={{ from: location }} replace />
  }

  if (user && !profileLoading && !onboardingCompleted) {
    return <Navigate to={ROUTES.ONBOARDING} replace />
  }

  const navigation = user ? authenticatedNav : publicNav

  const isActive = (item: (typeof navigation)[number]) => {
    if (location.pathname === item.href) return true
    return item.childRoutes.some(
      (route) => location.pathname === route || location.pathname.startsWith(`${route}/`)
    )
  }

  const content = (
    <div className="flex min-h-full flex-col bg-bg text-fg-primary">
      <Outlet />
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 safe-bottom"
        style={{
          background: 'color-mix(in srgb, var(--color-bg) 92%, transparent)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderTop: '1px solid var(--cream-300)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div
          className="grid h-16"
          style={{
            gridTemplateColumns: `repeat(${navigation.length}, 1fr)`,
            padding: '8px 4px 4px',
          }}
        >
          {navigation.map((item) => {
            const active = isActive(item)
            const Icon = item.icon
            return (
              <Link
                key={item.name}
                to={item.href}
                className="relative flex flex-col items-center gap-0.5 pt-1.5 pb-1 transition-colors"
                style={{ color: active ? 'var(--color-fg-primary)' : 'var(--color-fg-tertiary)' }}
              >
                <Icon className="h-[19px] w-[19px]" strokeWidth={active ? 2 : 1.5} />
                <span
                  className="text-[10px] tracking-wide"
                  style={{ fontWeight: active ? 600 : 400 }}
                >
                  {item.name}
                </span>
                {active && (
                  <span
                    className="absolute top-0 w-[18px] h-[2px] rounded-sm"
                    style={{ background: 'var(--color-primary)' }}
                  />
                )}
              </Link>
            )
          })}
        </div>
      </nav>
    </div>
  )

  if (user) {
    return (
      <CelebrationProvider>
        <GameCelebrationWatcher />
        {content}
      </CelebrationProvider>
    )
  }

  return content
}
